/**
 * Shared helpers for the apps/web/api/* wrappers.
 *
 * The Railway routes return `{ data: T }` (or `{ data, totalCount }`) on
 * success and HTTP-status errors via ApiError. The frontend service-layer
 * call sites still expect the old shape `{ data, error }`. These helpers
 * translate between the two so we don't have to touch every call site.
 */

import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiClient";

export interface CreateEnvelope<T> {
  data: T | null;
  error: Error | null;
}

export interface UpdateEnvelope<T> {
  data: T | null;
  error: Error | null;
  changesLogged: boolean;
}

export interface DeleteEnvelope {
  success: boolean;
  error: Error | null;
  changesLogged: boolean;
}

export interface GetEnvelope<T> {
  data: T | null;
  error: Error | null;
}

export interface ListEnvelope<T> {
  data: T[];
  error: Error | null;
}

export interface PaginatedEnvelope<T> {
  data: T[];
  totalCount: number;
  error: Error | null;
}

function toError(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

export async function tryGet<T>(path: string, signal?: AbortSignal): Promise<GetEnvelope<T>> {
  try {
    const result = await apiGet<{ data: T }>(path, signal);
    return { data: result.data, error: null };
  } catch (err) {
    return { data: null, error: toError(err) };
  }
}

export async function tryList<T>(path: string, signal?: AbortSignal): Promise<ListEnvelope<T>> {
  try {
    const result = await apiGet<{ data: T[] }>(path, signal);
    return { data: result.data, error: null };
  } catch (err) {
    return { data: [], error: toError(err) };
  }
}

export async function tryPaginated<T>(
  path: string,
  signal?: AbortSignal,
): Promise<PaginatedEnvelope<T>> {
  try {
    const result = await apiGet<{ data: T[]; totalCount: number }>(path, signal);
    return { data: result.data, totalCount: result.totalCount, error: null };
  } catch (err) {
    return { data: [], totalCount: 0, error: toError(err) };
  }
}

export async function tryCreate<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<CreateEnvelope<T>> {
  try {
    const result = await apiPost<{ data: T }>(path, body, signal);
    return { data: result.data, error: null };
  } catch (err) {
    return { data: null, error: toError(err) };
  }
}

export async function tryUpdate<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<UpdateEnvelope<T>> {
  try {
    const result = await apiPatch<{ data: T; changesLogged?: boolean }>(path, body, signal);
    return { data: result.data, error: null, changesLogged: !!result.changesLogged };
  } catch (err) {
    return { data: null, error: toError(err), changesLogged: false };
  }
}

export async function tryUpdatePost<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<UpdateEnvelope<T>> {
  try {
    const result = await apiPost<{ data: T; changesLogged?: boolean }>(path, body, signal);
    return { data: result.data, error: null, changesLogged: !!result.changesLogged };
  } catch (err) {
    return { data: null, error: toError(err), changesLogged: false };
  }
}

export async function tryDelete(
  path: string,
  signal?: AbortSignal,
): Promise<DeleteEnvelope> {
  try {
    const result = await apiDelete<{ success: boolean; changesLogged?: boolean }>(path, signal);
    return { success: result.success, error: null, changesLogged: !!result.changesLogged };
  } catch (err) {
    return { success: false, error: toError(err), changesLogged: false };
  }
}

export function toQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}
